import { AnyAction, Store } from 'redux';
import State from 'store/State';
import { AppMode } from 'app/store/general/AppMode';
import { selectIsFeatureDisabled } from 'app/store/featureToggles/selectors';
import { userHasAllRequiredAuthorities } from 'login/store/selectors';
import { PortfolioInput } from 'portfolio/PortfolioInput';
import { WidgetType } from './WidgetType';
import {
    isWidgetStrategyWithConfig,
    PortfolioInputWidget,
    RenderFilter,
    WidgetHost,
    WidgetStrategy,
    WidgetStrategyWithConfig,
    WidgetStrategyWithCustomManagedStream,
    WidgetStrategyWithStreams,
} from './WidgetRegistry';
import { WidgetInfo } from './WidgetInfo';
import { WidgetPermissionDenied } from 'widgets/WidgetNotAvailable/WidgetPermissionDenied';
import { WidgetNotAvailableInEnvironment } from 'widgets/WidgetNotAvailable/WidgetNotAvailableInEnvironment';
import { unknownWidgetStrategy } from './unknown/UnknownWidgetStrategy';
import { WidgetSharedStateSection } from './store/shared/WidgetsSharedState';
import { WidgetActionType } from 'widgets/store/actions';
import { AppModeWidgetCategory, WidgetCategory } from './WidgetCategory';
import { DropdownTooltipText } from '@instinet-analytics/semantic-ui-components';
import { WidgetStreamConfig } from 'widgets/streaming/WidgetStreamConfig';
import { WidgetComponentProps } from 'widgets/WidgetComponentProps';
import {
    selectWidgetInstanceConfig,
    selectWidgetInstanceState,
} from 'widgets/store/selectors';
import { selectAllWidgetIds } from 'layout/store/selectors';

import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import { OrderColumns } from 'communication/types/order/Order';
import { toOrderColumn } from 'app/store/orders/mapping';
import { WidgetId } from 'widgets/WidgetId';
import {
    isWidgetByRequestUidConfig,
    isWidgetWithMarketDataRequestConfig,
    StreamRequestInfo,
    WidgetByRequestUidConfig,
    WidgetCustomManagedConfig,
    WidgetWithMarketDataRequestConfig,
} from 'widgets/streaming/widgetStreamManager/WidgetCustomStreamManager';
import { MarketDataRequest } from 'marketData/MarketDataRequest';
import { WidgetConfigWithTabTitle } from 'widgets/tabTitle/WidgetConfigWithTabTitle';
import { DataServices } from 'store/Services';
import { PortfolioSelection } from 'portfolio/PortfolioSelection';
import { SymbolsService } from 'symbols/SymbolsService';
import { PortfolioSelectionWrapper } from 'portfolio/PortfolioSelectionWrapper';

export interface WidgetsServiceOptions {
    widgets: {
        [key in WidgetType]: WidgetStrategy;
    };
    symbolsService: SymbolsService;
}

export interface WidgetDropdownItem {
    type: WidgetType;
    title: string;
    categories: WidgetCategory[] | undefined;
    description?: DropdownTooltipText;
}

export type WidgetTypeStrategy = { type: WidgetType; strategy: WidgetStrategy };

/*
 * Service used for all widget interactions, decoupling the widget implementations from their interactions with the rest of the app
 */
export class WidgetsService {
    private widgets: {
        [key in WidgetType]: WidgetStrategy;
    };
    private allStrategies: WidgetTypeStrategy[];
    public store: Store<State, AnyAction> | undefined;
    private symbolsService: SymbolsService;

    public constructor(options: WidgetsServiceOptions) {
        this.widgets = options.widgets;
        this.symbolsService = options.symbolsService;
        this.allStrategies = Object.keys(options.widgets).map((k) => {
            const type = k as WidgetType;
            return { type, strategy: options.widgets[type] };
        });
    }

    /*
     * Get the titles of all available widgets by type and host type - available meaning not the Blotter in Newport, and not widgets with feature toggle off
     */
    public getTitles(appMode: AppMode, host: WidgetHost): WidgetDropdownItem[] {
        return this.getStrategiesForAvailableWidgets(appMode, host).map((s) => {
            const categories = getCategories(appMode, s.strategy);
            return {
                type: s.type,
                title: s.strategy.title,
                categories: categories,
                description: s.strategy.description,
            };
        });
    }

    /*
     * Get the react component type for a widget type
     * Will return a WidgetNotAvailableInEnvironment for those with filter toggle off
     */
    public getReactComponentType(
        type: WidgetType
    ): React.FC<WidgetComponentProps> {
        return this.getReactComponentTypeToRender(
            type,
            this.getWidgetStrategy(type)
        );
    }

    /*
     * Gets the complete list of React components by widget type
     * (used for registration with golden layout)
     * Will return a WidgetNotAvailableInEnvironment for those with filter toggle off
     */
    public getReactComponents(): {
        type: WidgetType;
        reactComponentType: React.FC<WidgetComponentProps>;
    }[] {
        return this.allStrategies.map((s) => {
            return {
                type: s.type,
                reactComponentType: this.getReactComponentTypeToRender(
                    s.type,
                    s.strategy
                ),
            };
        });
    }

    /*
     * Returns TRUE if the given action affects the layout
     */
    public isWidgetActionAffectingLayout(action: AnyAction) {
        const actionsAffectingLayout = [
            WidgetActionType.WIDGET_TOGGLE_GRID_COLUMN_SORT,
            WidgetActionType.WIDGET_SET_GRID_COLUMN_WIDTH,
            WidgetActionType.WIDGET_SET_CONFIG,
            WidgetActionType.WIDGET_TOGGLE_GRID_SINGLE_COLUMN_SORT,
        ];
        return actionsAffectingLayout.includes(action.type);
    }

    /*
     * Gets the list of widgets in the layout that consume the orders stream
     */
    public getOrderConsumingWidgets(widgets: WidgetInfo[]) {
        return widgets.filter(
            (x) =>
                !!this.getWidgetStrategy(x.type).getRequiredOrderColumns ||
                !!this.getWidgetStrategy(x.type).getRequiredPortfolioColumns
        );
    }

    /*
     * A function that gets the list of columns required in the orders stream for a widget type
     */
    public getRequiredOrderColumns(
        type: WidgetType,
        state: State,
        widgetId: string,
        symbology: SymbologyValues
    ): OrderColumns {
        const columns: OrderColumns = [];
        const strategy = this.getWidgetStrategy(type);

        if (strategy.getRequiredOrderColumns) {
            columns.push(
                ...strategy.getRequiredOrderColumns(state, widgetId, symbology)
            );
        }
        if (strategy.getRequiredPortfolioColumns) {
            columns.push(
                ...strategy
                    .getRequiredPortfolioColumns(state, widgetId, symbology)
                    .map((col) => toOrderColumn(col))
            );
        }
        return columns;
    }

    /*
     * Returns TRUE if the given widget type is an input widget
     */
    public isInput(type: WidgetType) {
        return this.getWidgetStrategy(type).isInput;
    }

    /*
     * Get the selector to select the config to be persisted in a layout
     */
    public getInitialConfigState<TConfig>(
        type: WidgetType
    ): TConfig | undefined {
        return (
            this.getWidgetStrategy(type) as WidgetStrategyWithConfig<TConfig>
        ).initialConfigState;
    }

    public getStreamConfigs(
        widgetType: WidgetType
    ): Array<WidgetStreamConfig<unknown, unknown>> {
        return (
            (this.getWidgetStrategy(widgetType) as WidgetStrategyWithStreams)
                .streamConfigs ?? []
        );
    }

    public getCustomManagedStreamConfig(
        widgetType: WidgetType
    ): WidgetCustomManagedConfig<unknown> | undefined {
        return (
            this.getWidgetStrategy(
                widgetType
            ) as WidgetStrategyWithCustomManagedStream
        ).managedStreamConfig;
    }

    /*
     * Render a filter for use in the ActiveFiltersIndicator
     */
    public getRenderFilter(type: WidgetType): RenderFilter | undefined {
        return this.getWidgetStrategy(type).renderFilter;
    }

    public getWidgetTitle(type: WidgetType) {
        const strategy = this.getWidgetStrategy(type);
        return strategy.title;
    }

    /*
     * Gets a widget tab title, based on the selection and widget config
     */
    public async getTitle(
        type: WidgetType,
        selection?: PortfolioSelection,
        config?: WidgetConfigWithTabTitle
    ): Promise<string> {
        const strategy = this.getWidgetStrategy(type);

        const title = config?.tabTitle ?? strategy.title;

        if (strategy === unknownWidgetStrategy) {
            return type;
        }

        if (isWidgetStrategyWithConfig(strategy)) {
            const titleFromConfig = strategy.getTitle
                ? await strategy.getTitle(
                      this.symbolsService,
                      selection,
                      config
                  )
                : undefined;
            if (titleFromConfig) {
                return titleFromConfig;
            }
        }

        const selectionWrapper = new PortfolioSelectionWrapper(selection);
        if (selectionWrapper.selectedSymbol) {
            return this.symbolsService.processTemplate(
                selectionWrapper.selectedSymbol,
                title,
                selectionWrapper.symbology
            );
        }

        return this.symbolsService.applyTemplate(title, '(no selection)');
    }

    public getSharedStateSections(
        type: WidgetType
    ): WidgetSharedStateSection[] {
        const strategy = this.getWidgetStrategy(type);
        return strategy.sharedStateSections ? strategy.sharedStateSections : [];
    }

    /*7
     * Look up the strategy for the widget type
     */
    public getStrategyType(type: WidgetType): WidgetType {
        return this.widgets[type] ? type : WidgetType.UNKNOWN;
    }

    /*
     * Returns the PortfolioInput if the given widget is an input, undefined otherwise
     */

    public getPortfolioInput(
        widgetInfo: WidgetInfo
    ): PortfolioInput | undefined {
        const getPortfolioInput = (
            this.widgets[widgetInfo.type] as PortfolioInputWidget
        ).getPortfolioInput;
        if (!getPortfolioInput) {
            throw new Error('Widget is not an input widget');
        }
        return getPortfolioInput(widgetInfo.id);
    }

    public getPortfolioSelectionRequirements(type: WidgetType) {
        return this.getWidgetStrategy(type).portfolioSelectionRequirements;
    }

    public getPortfolioSelectionRequirementsWithoutOrdersStream(
        type: WidgetType
    ) {
        return this.getWidgetStrategy(type)
            .portfolioSelectionRequirementsWithoutOrdersStream;
    }

    public getNextWidgetId(widgetType: WidgetType) {
        const existing = selectAllWidgetIds(this.store!.getState());
        let nextId = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
            // Increment until we find an unused id...
            const nextKey = `${widgetType}-${nextId}`;
            // We can't use anything in the kayiout...
            const existsInLayout = !!existing.find((e) => e === nextKey);
            // Also check that there is not any state hanging around from removed widgets...
            const existsInState = !!selectWidgetInstanceState(
                this.store!.getState(),
                widgetType,
                nextKey
            );
            if (!existsInLayout && !existsInState) {
                return nextKey;
            }
            nextId++;
        }
    }

    /*
     * Gets the strategies for widgets available in this environment
     * Excludes those feature toggled off, user does not have permission, hidden in Newport if we are in Newport, and Unknown
     */
    public getStrategiesForAvailableWidgets(
        appMode: AppMode,
        host: WidgetHost
    ): WidgetTypeStrategy[] {
        return this.allStrategies
            .filter((s) => !this.isWidgetHidden(s.type))
            .filter(
                (s) =>
                    appMode !== AppMode.EMBEDDED_DASHBOARD ||
                    !s.strategy.hiddenInNewportLinkedPalette
            )
            .filter((s) => s.strategy.supportedHosts.indexOf(host) > -1)
            .filter((s) => s.type !== WidgetType.UNKNOWN);
    }

    private getReactComponentTypeToRender(
        type: WidgetType,
        strategy: WidgetStrategy
    ) {
        if (type !== WidgetType.SUPPORT_VISUALISATION) {
            if (this.isHiddenByFeatureToggle(type)) {
                return WidgetNotAvailableInEnvironment;
            }
            if (this.isHiddenByPermissions(type)) {
                return WidgetPermissionDenied;
            }
        }
        return strategy.reactComponentType;
    }

    private isWidgetHidden(type: WidgetType) {
        return (
            this.isHiddenByFeatureToggle(type) ||
            this.isHiddenByPermissions(type)
        );
    }

    private isHiddenByFeatureToggle(type: WidgetType) {
        if (!this.store) {
            throw new Error('store must be set');
        }
        const featureKey = 'widget.' + type;
        return selectIsFeatureDisabled(this.store.getState(), featureKey);
    }

    private isHiddenByPermissions(type: WidgetType) {
        if (!this.store) {
            throw new Error('store must be set');
        }
        const authorities = this.widgets[type]?.requiredAuthorities;
        return (
            authorities &&
            !userHasAllRequiredAuthorities(this.store.getState(), authorities)
        );
    }

    /*
     * Returns the strategy for the widget type, or the UNKNOWN strategy if we don't recognise the widget type
     */
    public getWidgetStrategy(widgetType: WidgetType) {
        const strategyType = this.getStrategyType(widgetType);
        return this.widgets[strategyType];
    }

    public async getMarketDataRequests(
        services: DataServices,
        id: WidgetId,
        activeInputUsesOrdersStream: boolean
    ): Promise<MarketDataRequest[]> {
        const config = this.getMarketDataRequestConfig(id.widgetType);
        return config
            ? await config.getMarketDataRequests(
                  this.store!.getState(),
                  services,
                  id,
                  activeInputUsesOrdersStream
              )
            : [];
    }

    public async getRequestsWithRequestUid(
        services: DataServices,
        id: WidgetId,
        activeInputUsesOrdersStream: boolean
    ): Promise<
        StreamRequestInfo<unknown>[] | StreamRequestInfo<unknown> | undefined
    > {
        const config = this.getStreamByRequestUidConfig(id.widgetType);
        return config
            ? await config.getWidgetStreamRequestInfo(
                  this.store!.getState(),
                  services,
                  id,
                  activeInputUsesOrdersStream
              )
            : undefined;
    }

    public async getRequestsByRequestUidForRegisteredSymbolStreams(
        services: DataServices,
        registeredSymbolStream: {
            widgetType: WidgetType;
            symbols: string[];
            config: unknown;
            quantities?: number[];
        }
    ): Promise<StreamRequestInfo<unknown>[]> {
        const { widgetType, symbols, config, quantities } =
            registeredSymbolStream;
        const streamConfig = this.getStreamByRequestUidConfig(widgetType);
        return streamConfig
            ? await streamConfig.getStreamRequestInfosForSymbols(
                  this.store!.getState(),
                  services,
                  symbols,
                  config,
                  quantities
              )
            : [];
    }

    public async getMarketDataRequestsForRegisteredSymbolStreams(
        dataServices: DataServices,
        registeredSymbolStream: {
            widgetType: WidgetType;
            symbols: string[];
            config: unknown;
        }
    ): Promise<MarketDataRequest[]> {
        const { widgetType, symbols, config } = registeredSymbolStream;
        const streamConfig = this.getMarketDataRequestConfig(widgetType);
        if (!streamConfig) {
            return [];
        }
        return await streamConfig.getMarketDataRequestForSymbols(
            this.store!.getState(),
            dataServices,
            symbols,
            config
        );
    }

    public getStreamByRequestUidConfig(
        widgetType: WidgetType
    ): WidgetByRequestUidConfig<unknown> | undefined {
        const config = this.getCustomManagedStreamConfig(widgetType);
        return config && isWidgetByRequestUidConfig(config)
            ? config
            : undefined;
    }

    public getMarketDataRequestConfig(
        widgetType: WidgetType
    ): WidgetWithMarketDataRequestConfig | undefined {
        const config = this.getCustomManagedStreamConfig(widgetType);
        return config && isWidgetWithMarketDataRequestConfig(config)
            ? config
            : undefined;
    }

    public getWidgetConfig(widgetId: WidgetId) {
        const state = this.store!.getState();
        return selectWidgetInstanceConfig(state, widgetId);
    }
}

function getCategories(
    appMode: AppMode,
    strategy: WidgetStrategy
): WidgetCategory[] {
    const ret: WidgetCategory[] = [];
    if (strategy.categories && strategy.categories.length > 0) {
        for (const cat of strategy.categories) {
            const appModeS = cat as AppModeWidgetCategory;
            if (appModeS.appModes) {
                if (appModeS.appModes.includes(appMode)) {
                    ret.push(...appModeS.categories);
                }
            } else {
                ret.push(cat as WidgetCategory);
            }
        }
    }
    return ret;
}
